// src/render/audio/waffle.ts — Phase 6 per-character waffle voice (06-05).
//
// CONTEXT D-05..D-08:
//   - Single shared syllable atlas (one Howl, audio sprite). Loaded once at
//     audio-manager init via srcListFor('waffle/atlas', manifest); pool of
//     P6_HOWL_POOL_WAFFLE = 4 voices (D-20).
//   - Per-character pitch via getVoicePitch(characterId) — additive
//     CharacterSchema.voicePitch field (06-01). Per-syllable jitter ∈
//     [P6_WAFFLE_PITCH_JITTER_MIN..MAX] from rngFn(). Howl.rate(pitch * jitter).
//   - Adult-pitch override (D-07): characterId starting with `adult-` uses the
//     single shared P6_ADULT_VOICE_PITCH constant.
//   - Syllable count derived from text char count (~1 syllable per 4 chars,
//     min 1). Sequenced at P6_WAFFLE_SYLLABLE_INTERVAL_MS cadence (= 1000 /
//     P6_WAFFLE_SYLLABLES_PER_SEC = 125 ms).
//
// P6-Pitfall A — cancel safety: per-syllable setTimeout ids are tracked in
// `activeLines.get(entityId).timeouts`; in-flight Howl.play sound ids are
// tracked in `activeLines.get(entityId).soundIds`. cancelWaffleLine clears
// every pending timeout AND calls Howl.stop on every in-flight id. Without
// this the player hears waffle continuing past the bubble that triggered it
// when the BubbleLayer (06-04) preempts mid-typewriter.
//
// BLOCKER-1 (Plan 03 trackHowl contract): the constructed atlas Howl is
// registered with audioManager.trackHowl so audioManager.getLiveSources()
// returns a real count of active playback ids — used by 06-08
// phase6-integration.test.ts for the AUDIO-07 ≤ 8 concurrent ceiling proof.
// Cycle-safe at runtime: this file imports `audioManager` from manager.ts;
// manager.ts imports `initWaffle` from this file. The cycle is safe because
// `audioManager` is only dereferenced INSIDE initWaffle's body (never at
// module top-level), and by the time initWaffle runs, the manager singleton
// is fully constructed (manager.init() calls initWaffle synchronously).
//
// Source: 06-CONTEXT.md D-05..D-08, D-20; 06-RESEARCH.md Pattern 2 + Example 5;
//         06-PATTERNS.md §src/render/audio/waffle.ts.

import { Howl } from 'howler';

import type { AudioCodec, AudioManifest } from './codec';
import { srcListFor } from './codec';
import { getVoicePitch } from '../../sim/content/cache';
import { audioManager } from './manager';
import {
  P6_WAFFLE_SYLLABLE_INTERVAL_MS,
  P6_WAFFLE_PITCH_JITTER_MIN,
  P6_WAFFLE_PITCH_JITTER_MAX,
  P6_HOWL_POOL_WAFFLE,
  P6_ADULT_VOICE_PITCH,
} from './constants';

// ---------------------------------------------------------------------------
// Module-level state (singleton — DIAL-10)
// ---------------------------------------------------------------------------

let waffleHowl: Howl | null = null;

interface ActiveLine {
  timeouts: ReturnType<typeof setTimeout>[];
  soundIds: number[];
}
const activeLines: Map<string, ActiveLine> = new Map();

let rngFn: () => number = Math.random;

/**
 * Adult-id detection (D-07).
 *
 * Placeholder approach: any characterId beginning with `adult-` is treated as
 * an adult and uses the shared P6_ADULT_VOICE_PITCH override on top of the
 * jitter. The canonical adult registry will land in a later P6 plan or P7
 * tuning pass; until then this prefix-based check covers the existing adult
 * NPCs that 04 wires (`adult-claire`, `adult-jose`, `adult-marie`,
 * `adult-pedro`, etc.) without requiring a registry import.
 *
 * TODO(P6-content / P7 POL-06): swap to a profile-flag read once the
 * canonical adult registry is authored in default-bundle.json.
 */
function isAdult(characterId: string): boolean {
  return characterId.startsWith('adult-');
}

const SYLLABLE_NAMES = [
  'syl-ba', 'syl-bo', 'syl-do', 'syl-ki',
  'syl-la', 'syl-mu', 'syl-ne', 'syl-pa',
] as const;

/** D-05 / DIAL-07: ~1 syllable per 4 characters of text, minimum 1. */
function syllableCountForText(text: string): number {
  return Math.max(1, Math.ceil(text.length / 4));
}

export interface WaffleHandle {
  /** Total scheduled duration in ms (= syllableCount * interval). */
  durationMs: number;
  /** P6-Pitfall A — clears every pending timeout + stops every in-flight sprite. */
  cancel(): void;
}

// ---------------------------------------------------------------------------
// Public API (matches 06-03 manager.ts seam)
// ---------------------------------------------------------------------------

export function initWaffle(_codec: AudioCodec, manifest: AudioManifest): void {
  // DIAL-10 singleton: a second init() call leaves the existing atlas in place.
  if (waffleHowl) return;

  waffleHowl = new Howl({
    src: srcListFor('waffle/atlas', manifest),
    sprite: {
      // Hand-curated 8-syllable pack — matches 'waffle/atlas' manifest entry.
      // Each [offsetMs, durationMs] pair. Authored in 06-08 to match these offsets.
      'syl-ba': [0, 180],
      'syl-bo': [200, 180],
      'syl-do': [400, 180],
      'syl-ki': [600, 180],
      'syl-la': [800, 180],
      'syl-mu': [1000, 180],
      'syl-ne': [1200, 180],
      'syl-pa': [1400, 180],
    },
    pool: P6_HOWL_POOL_WAFFLE,
    preload: false, // P6-Pitfall 8 — load only after iOS unlock; load() called below.
    volume: 0.8,
  });
  waffleHowl.load();
  // BLOCKER-1: register every Howl with the manager liveSources Set so the
  // AUDIO-07 ≤ 8 concurrent-source ceiling test (Plan 08) sees real counts.
  // UI-06: the waffle atlas is SFX at its construction design volume 0.8.
  audioManager.trackHowl(waffleHowl, 'sfx', 0.8);
}

/**
 * Sequence syllables beneath an active bubble. Returns a handle whose
 * `cancel()` aborts the line (P6-Pitfall A). Calling playWaffleLine again for
 * the same entityId auto-cancels the prior line (BubbleLayer preempt path).
 *
 * Returns null when the atlas is not yet initialised — graceful degradation
 * during the boot window between AudioUnlockSplash dismiss and audioManager
 * init completion.
 */
export function playWaffleLine(
  entityId: string,
  characterId: string,
  text: string,
): WaffleHandle | null {
  if (!waffleHowl) return null;

  // Auto-cancel any in-flight line for this entity (preempt path from BubbleLayer
  // OR back-to-back character-spoke for the same speaker).
  cancelWaffleLine(entityId);

  const syllableCount = syllableCountForText(text);
  const interval = P6_WAFFLE_SYLLABLE_INTERVAL_MS;
  const durationMs = syllableCount * interval;

  // D-07 adult-pitch override applies before per-syllable jitter.
  const voicePitch = isAdult(characterId)
    ? P6_ADULT_VOICE_PITCH
    : getVoicePitch(characterId);

  const line: ActiveLine = { timeouts: [], soundIds: [] };
  activeLines.set(entityId, line);

  for (let i = 0; i < syllableCount; i++) {
    const t = setTimeout(() => {
      // Capture the howl ref locally — destroyWaffle could null it concurrently.
      const howl = waffleHowl;
      if (!howl) return;
      const sylName = SYLLABLE_NAMES[Math.floor(rngFn() * SYLLABLE_NAMES.length)]!;
      const id = howl.play(sylName);
      // D-06 jitter formula on a single line so the acceptance grep matches.
      const jitter = P6_WAFFLE_PITCH_JITTER_MIN + rngFn() * (P6_WAFFLE_PITCH_JITTER_MAX - P6_WAFFLE_PITCH_JITTER_MIN);
      howl.rate(voicePitch * jitter, id);
      line.soundIds.push(id);
    }, i * interval);
    line.timeouts.push(t);
  }

  return {
    durationMs,
    cancel(): void {
      cancelWaffleLine(entityId);
    },
  };
}

/**
 * Stable seam called by manager.ts (and by BubbleLayer in 06-04) when a
 * bubble unmounts mid-typewriter or is preempted by a higher-tier bubble.
 * P6-Pitfall A: clears every pending timeout AND stops every in-flight sprite
 * sound id so audible waffle never outlives the bubble that triggered it.
 */
export function cancelWaffleLine(entityId: string): void {
  const line = activeLines.get(entityId);
  if (!line) return;
  for (const t of line.timeouts) clearTimeout(t);
  for (const id of line.soundIds) waffleHowl?.stop(id);
  activeLines.delete(entityId);
}

export function destroyWaffle(): void {
  // Cancel every active line first so no pending setTimeout can fire after
  // we null the atlas reference.
  for (const id of [...activeLines.keys()]) cancelWaffleLine(id);
  waffleHowl?.unload();
  waffleHowl = null;
}

// ---------------------------------------------------------------------------
// Test seam — production callers MUST NOT use this. Vitest tests inject a
// deterministic rngFn so jitter assertions are exact.
// ---------------------------------------------------------------------------

export function _setRngForTests(fn: () => number): void {
  rngFn = fn;
}
